import React, { useState, useEffect } from 'react';
import {
  Brain, BarChart2, Zap, Users, Package, Globe,
  Monitor, MessageCircle, TrendingUp, ChevronRight,
  X, Menu, Star, Check
} from 'lucide-react';
import Layout from '../../components/layout';

export default function GeniusLanding() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Layout>
      <div className="min-h-screen font-sans text-gray-800 overflow-x-hidden">
        {/* Header */}
        <header className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? "py-3 bg-white shadow-md" : "py-5 bg-transparent"
          }`}>
          <div className="container mx-auto px-6">
            <div className="flex justify-between items-center">
              <div className={`text-2xl font-bold transition-colors duration-300 ${scrolled ? "text-indigo-900" : "text-white"
                }`}>
                <Brain size={24} className="inline mr-2" />Genius
              </div>

              <button
                className={`md:hidden focus:outline-none transition-colors duration-300 ${scrolled ? "text-indigo-900" : "text-white"
                  }`}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>

              <nav className="hidden md:flex space-x-8 items-center">
                <NavLink href="#capabilities" label="Capabilities" scrolled={scrolled} />
                <NavLink href="#advantages" label="Advantages" scrolled={scrolled} />
                <NavLink href="#testimonials" label="Testimonials" scrolled={scrolled} />
                <a href="http://52.172.42.245:8080/" target='_blank' className="bg-purple-600 hover:bg-purple-700 text-white font-medium px-6 py-2 rounded-lg transition-all duration-300 hover:shadow-lg">
                  Get Started
                </a>
              </nav>
            </div>

            {isMenuOpen && (
              <nav className="md:hidden py-4 bg-white absolute top-full left-0 right-0 shadow-md border-t border-gray-100">
                <div className="container mx-auto px-6 flex flex-col space-y-3">
                  <MobileNavLink href="#capabilities" label="Capabilities" onClick={() => setIsMenuOpen(false)} />
                  <MobileNavLink href="#advantages" label="Advantages" onClick={() => setIsMenuOpen(false)} />
                  <MobileNavLink href="#testimonials" label="Testimonials" onClick={() => setIsMenuOpen(false)} />
                  <a
                    href="#contact"
                    className="bg-purple-600 hover:bg-purple-700 text-white text-center font-medium px-6 py-3 rounded-lg transition-all duration-300 hover:shadow-lg mt-3"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Get Started
                  </a>
                </div>
              </nav>
            )}
          </div>
        </header>

        {/* Hero Section */}
        <section className="min-h-screen flex items-center pt-20 bg-gradient-to-br from-purple-900 via-indigo-800 to-blue-600 text-white overflow-hidden relative">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -right-1/4 top-1/4 w-1/2 h-1/2 bg-purple-500 rounded-full opacity-20 blur-3xl"></div>
            <div className="absolute -left-1/4 -bottom-1/4 w-1/2 h-1/2 bg-indigo-700 rounded-full opacity-20 blur-3xl"></div>
          </div>

          <div className="container mx-auto px-6 py-12 z-10 relative">
            <div className="flex flex-col lg:flex-row items-center">
              <div className="lg:w-1/2 text-center lg:text-left mb-10 lg:mb-0">
                <span className="inline-block px-4 py-1 rounded-full bg-purple-800 bg-opacity-50 text-purple-200 text-sm font-medium mb-6">
                  AI-Powered Growth Engine
                </span>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                  Your Brain for D2C Success
                </h1>
                <p className="text-lg sm:text-xl text-indigo-100 mb-10 max-w-xl mx-auto lg:mx-0">
                  Genius unifies fragmented data into actionable insights, driving acquisition, retention, and efficiency for D2C and e-commerce brands.
                </p>
                <div className="flex flex-col sm:flex-row justify-center lg:justify-start gap-4">
                  <a href="/request-demo" className="bg-white text-purple-800 hover:bg-purple-50 font-bold px-8 py-3 rounded-lg transition transform hover:-translate-y-1 shadow-lg">
                    Request Demo
                  </a>
                  <a href="http://52.172.42.245:8080/" className="border-2 border-white text-white hover:bg-white hover:bg-opacity-10 font-bold px-8 py-3 rounded-lg transition transform hover:-translate-y-1">
                    Login to Genius
                  </a>
                </div>
              </div>

              <div className="lg:w-1/2 relative">
                <div className="relative z-10 bg-white p-4 rounded-2xl shadow-2xl">
                  <img src="/genius/frontImage.png" alt="Genius Dashboard" className="w-full rounded-lg" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Capabilities Section */}
        <section id="capabilities" className="py-24 bg-white">
          <div className="container mx-auto px-6">
            <SectionHeader
              title="Core Capabilities"
              subtitle="Unlock the full potential of your D2C business with AI-driven analytics and optimization"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mt-16">
              <CapabilityCard
                icon={<BarChart2 size={28} />}
                title="Advanced Analytics"
                description="Unified dashboards for channel performance, inventory, and store-level insights."
              />
              <CapabilityCard
                icon={<MessageCircle size={28} />}
                title="GenAI Assistants"
                description="Conversational AI for search, commerce, and dynamic content generation."
              />
              <CapabilityCard
                icon={<Users size={28} />}
                title="Personalization"
                description="Tailored campaigns and predictive recommendations to boost AOV."
              />
              <CapabilityCard
                icon={<Zap size={28} />}
                title="Simulation Engine"
                description="Price, campaign, and assortment planning with real-time simulations."
              />
              <CapabilityCard
                icon={<Package size={28} />}
                title="Supply Chain AI"
                description="Real-time visibility and automated order tracking for efficiency."
              />
              <CapabilityCard
                icon={<Globe size={28} />}
                title="Geospatial Insights"
                description="Heatmaps and demographic analysis for targeted growth."
              />
            </div>
          </div>
        </section>

        {/* Advantages Section */}
        <section id="advantages" className="py-24 bg-gradient-to-br from-purple-900 via-indigo-800 to-blue-600 text-white">
          <div className="container mx-auto px-6">
            <SectionHeader
              title="Business Advantages"
              subtitle="Transform your operations and growth strategy with Genius"
              light={true}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
              <AdvantageCard
                icon={<Monitor size={24} />}
                title="Unified Data Layer"
                description="Consolidates 20+ sources into one platform."
              />
              <AdvantageCard
                icon={<Zap size={24} />}
                title="Faster Decisions"
                description="AI insights reduce reliance on analysts."
              />
              <AdvantageCard
                icon={<TrendingUp size={24} />}
                title="Smarter Campaigns"
                description="Maximize ROI with personalized marketing."
              />
              <AdvantageCard
                icon={<Users size={24} />}
                title="Customer Retention"
                description="Boost loyalty with CLTV predictions."
              />
              <AdvantageCard
                icon={<Package size={24} />}
                title="Inventory Efficiency"
                description="Optimize stock with demand forecasting."
              />
              <AdvantageCard
                icon={<Globe size={24} />}
                title="SaaS & ONDC Ready"
                description="Seamless integration with modern stacks."
              />
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section id="testimonials" className="py-24 bg-gray-50">
          <div className="container mx-auto px-6">
            <SectionHeader
              title="What Our Clients Say"
              subtitle="Hear from D2C brands thriving with Genius"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-16">
              <TestimonialCard
                content="Genius turned our scattered data into a goldmine of insights. Our campaign ROI doubled within months!"
                name="Priya Sharma"
                role="CMO, D2C Apparel Brand"
                image="https://spratx.com/wp-content/uploads/2018/01/Testimonial-Woman-95974_500x675.jpg"
                rating={5}
              />
              <TestimonialCard
                content="The inventory forecasting and supply chain dashboards have cut our out-of-stock issues by 60%."
                name="Rahul Patel"
                role="COO, Health & Wellness Startup"
                image="https://www.kindpng.com/picc/m/586-5865544_testimonial-image-gentleman-hd-png-download.png"
                rating={5}
              />
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-purple-600 text-white text-center">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Supercharge Your D2C Growth</h2>
            <p className="text-xl mb-10 max-w-2xl mx-auto">
              Leverage Genius to optimize operations, boost retention, and scale smarter.
            </p>
            <a href="/request-demo" className="bg-white text-purple-600 hover:bg-purple-50 font-bold px-8 py-4 rounded-lg transition transform hover:-translate-y-1 shadow-lg">
              Request a Demo
            </a>
          </div>
        </section>
      </div>
    </Layout>
  );
}

// Reusable Components
function SectionHeader({ title, subtitle, light = false }) {
  return (
    <div className="text-center max-w-3xl mx-auto">
      <h2 className={`text-3xl md:text-4xl font-bold mb-6 ${light ? 'text-white' : 'text-indigo-900'}`}>
        {title}
      </h2>
      <p className={`text-lg ${light ? 'text-indigo-100' : 'text-gray-600'}`}>
        {subtitle}
      </p>
    </div>
  );
}

function CapabilityCard({ icon, title, description }) {
  return (
    <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition duration-300">
      <div className="w-12 h-12 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center mb-4">
        {icon}
      </div>
      <h3 className="text-xl font-bold text-indigo-900 mb-2">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
}

function AdvantageCard({ icon, title, description }) {
  return (
    <div className="bg-white bg-opacity-10 rounded-xl p-6">
      <div className="bg-white bg-opacity-20 text-white p-3 rounded-lg inline-block mb-4">
        {icon}
      </div>
      <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
      <p className="text-indigo-100">{description}</p>
    </div>
  );
}

function TestimonialCard({ content, name, role, image, rating }) {
  return (
    <div className="bg-white rounded-xl p-6 shadow-lg">
      <div className="flex mb-4">
        {[...Array(rating)].map((_, i) => (
          <Star key={i} size={16} className="text-yellow-500 fill-current" />
        ))}
      </div>
      <p className="text-gray-700 mb-6">{content}</p>
      <div className="flex items-center">
        <img src={image} alt={name} className="w-12 h-12 rounded-full mr-4 object-cover" />
        <div>
          <h4 className="font-bold text-indigo-900">{name}</h4>
          <p className="text-gray-600 text-sm">{role}</p>
        </div>
      </div>
    </div>
  );
}

function NavLink({ href, label, scrolled }) {
  return (
    <a
      href={href}
      className={`transition-colors duration-300 font-medium ${scrolled ? "text-gray-700 hover:text-purple-600" : "text-white hover:text-purple-200"
        }`}
    >
      {label}
    </a>
  );
}

function MobileNavLink({ href, label, onClick }) {
  return (
    <a
      href={href}
      className="text-gray-800 hover:text-purple-600 py-2 flex justify-between items-center"
      onClick={onClick}
    >
      {label}
      <ChevronRight size={18} />
    </a>
  );
}